import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'Button';
import { Dropdown, type SingleSectionItems } from 'Dropdown';
import { type SelectOption } from './Select.types';

type Props = {
    options: SelectOption[];
    value: string;
    onValueChange: (value: string) => void;
    'aria-label': string;
    className?: string;
};

// A themed, opinionated single-value form control built on the shared `Dropdown`. It fixes the
// trigger to a `bordered-white-light` `Button` that renders the selected option's label plus a
// chevron, and exposes a plain `string`-in / `string`-out API (`value` / `onValueChange`) instead
// of `Dropdown`'s `selectedKeys: string[]` + caller-supplied trigger surface. Use this for the
// common "pick one value" case so call sites don't re-hand-roll that boilerplate.
//
// Reach for `Dropdown` directly (not `Select`) when you need any of its flexibility: a custom
// trigger that doesn't display the selected value, multiple selection, multi-section menus, or
// controlled open state (e.g. `AudioSelectButton`'s device picker).
export function Select({ options, value, onValueChange, 'aria-label': ariaLabel, className }: Props) {
    const selectedOption = options.find(option => option.value === value);
    const items: SingleSectionItems = options.map(option => ({ key: option.value, label: option.label }));

    return (
        <Dropdown
            type="single-section"
            selectionMode="single"
            selectedKeys={[value]}
            onSelectionChange={([selectedKey]) => {
                if (selectedKey) onValueChange(selectedKey);
            }}
            items={items}
        >
            <Button
                variant="bordered-white-light"
                color="black"
                height={9}
                fontWeight="normal"
                aria-label={ariaLabel}
                className={className}
            >
                <span>{selectedOption?.label ?? value}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs text-slate-grey" />
            </Button>
        </Dropdown>
    );
}
